import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppModal from '../components/AppModal';
import api from '../services/api';
import { abrirPdfRecibo } from '../utils/abrirDocumento';

function fechaHoyInput() {
  const fecha = new Date();

  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
}

function fechaInicioMesInput() {
  const fecha = new Date();

  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-01`;
}

function formatoFecha(fecha) {
  if (!fecha) return '';

  const texto = String(fecha).replace('T', ' ').replace('Z', '');
  const [fechaParte] = texto.split(' ');
  const [anio, mes, dia] = fechaParte.split('-');

  return `${dia}/${mes}/${anio}`;
}

function obtenerCodigoMoneda(moneda) {
  const texto = String(moneda || '').toUpperCase();

  if (
    texto.includes('USD') ||
    texto.includes('DOLAR') ||
    texto.includes('DÓLAR')
  ) {
    return 'USD';
  }

  return 'MXN';
}

function formatoImporte(importe, moneda) {
  const numero = Number(importe || 0);
  const codigoMoneda = obtenerCodigoMoneda(moneda);

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: codigoMoneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numero);
}

function escaparHtmlExcel(valor) {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function valorTextoExcel(valor) {
  const texto = String(valor ?? '');

  if (/^[=+\-@]/.test(texto)) {
    return `'${texto}`;
  }

  return texto;
}

function fechaHoraArchivo() {
  const fecha = new Date();

  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  const hora = String(fecha.getHours()).padStart(2, '0');
  const minuto = String(fecha.getMinutes()).padStart(2, '0');

  return `${anio}${mes}${dia}_${hora}${minuto}`;
}


function HistoricoPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [recibos, setRecibos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [cancelacionModal, setCancelacionModal] = useState({
    abierto: false,
    reciboId: null
  });
  const [avisoCancelacionModal, setAvisoCancelacionModal] = useState(false);

  const [filtros, setFiltros] = useState({
    FechaInicio: '',
    FechaFin: '',
    EmpresaEmiteId: '',
    PersonaEmiteId: '',
    EmpresaRecibeId: '',
    PersonaRecibeId: '',
    MonedaId: '',
    MonedaTexto: '',
    ConceptoId: '',
    FormaPagoId: '',
    Folio: '',
    PendientesFirma: false,
    EstatusTexto: ''
  });

  const monedas = useMemo(
    () => catalogos.filter(item => item.Tipo === 'Moneda'),
    [catalogos]
  );

  const conceptos = useMemo(
    () => catalogos.filter(item => item.Tipo === 'Concepto'),
    [catalogos]
  );

  const formasPago = useMemo(
    () => catalogos.filter(item => item.Tipo === 'Forma de pago'),
    [catalogos]
  );

  const personasEmisoras = useMemo(() => {
    const mapa = new Map();

    personas
      .filter(item =>
        item.Tipo === 'Emisor' &&
        item.Activo
      )
      .forEach(item => {
        const clave = item.Nombre
          .trim()
          .toUpperCase();

        if (!mapa.has(clave)) {
          mapa.set(clave, item);
        }
      });

    return Array.from(mapa.values())
      .sort((a, b) =>
        a.Nombre.localeCompare(b.Nombre)
      );
  }, [personas]);

  const personasReceptoras = useMemo(() => {
    const mapa = new Map();

    personas
      .filter(item =>
        item.Tipo === 'Receptor' &&
        item.Activo
      )
      .forEach(item => {
        const clave = item.Nombre
          .trim()
          .toUpperCase();

        if (!mapa.has(clave)) {
          mapa.set(clave, item);
        }
      });

    return Array.from(mapa.values())
      .sort((a, b) =>
        a.Nombre.localeCompare(b.Nombre)
      );
  }, [personas]);

  useEffect(() => {
    iniciarHistorico();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const intervalo = setInterval(() => {
      buscarRecibos(filtros);
    }, 30000);

    function refrescarSiVisible() {
      if (document.visibilityState === 'visible') {
        buscarRecibos(filtros);
      }
    }

    window.addEventListener('focus', refrescarSiVisible);
    document.addEventListener('visibilitychange', refrescarSiVisible);

    return () => {
      clearInterval(intervalo);
      window.removeEventListener('focus', refrescarSiVisible);
      document.removeEventListener('visibilitychange', refrescarSiVisible);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  async function iniciarHistorico() {
    const filtrosUrl = obtenerFiltrosDesdeUrl();

    setFiltros(prev => ({
      ...prev,
      ...filtrosUrl
    }));

    await cargarDatosIniciales();
    await buscarRecibos(filtrosUrl);
  }

  function obtenerFiltrosDesdeUrl() {
    const queryParams = new URLSearchParams(location.search);

    const pendientesFirma = queryParams.get('pendientesFirma');
    const mesActual = queryParams.get('mesActual');
    const hoy = queryParams.get('hoy');
    const estatusTexto = queryParams.get('estatus');
    const monedaTexto = queryParams.get('monedaTexto');

    const filtrosUrl = {};

    if (pendientesFirma === '1') {
      filtrosUrl.PendientesFirma = true;
    }

    if (mesActual === '1') {
      filtrosUrl.FechaInicio = fechaInicioMesInput();
      filtrosUrl.FechaFin = '';
    }

    if (hoy === '1') {
      const hoyTexto = fechaHoyInput();

      filtrosUrl.FechaInicio = hoyTexto;
      filtrosUrl.FechaFin = hoyTexto;
    }

    if (estatusTexto) {
      filtrosUrl.EstatusTexto = estatusTexto;
    }

    if (monedaTexto) {
      filtrosUrl.MonedaTexto = monedaTexto;
    }

    return filtrosUrl;
  }

  async function cargarDatosIniciales() {
    try {
      const [empRes, perRes, catRes] = await Promise.all([
        api.get('/empresas'),
        api.get('/personas'),
        api.get('/catalogos?incluirInactivos=1')
      ]);

      setEmpresas(empRes.data || []);
      setPersonas(perRes.data || []);
      setCatalogos(catRes.data || []);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setFiltros({
      ...filtros,
      [name]: value
    });
  }

  async function buscarRecibos(filtrosExternos = null, e = null) {
    if (e) e.preventDefault();

    try {
      setMensaje('');

      const filtrosUsar = filtrosExternos || filtros;
      const params = {};

      if (filtrosUsar.FechaInicio) params.FechaInicio = filtrosUsar.FechaInicio;
      if (filtrosUsar.FechaFin) params.FechaFin = filtrosUsar.FechaFin;
      if (filtrosUsar.EmpresaEmiteId) params.EmpresaEmiteId = filtrosUsar.EmpresaEmiteId;
      if (filtrosUsar.EmpresaRecibeId) params.EmpresaRecibeId = filtrosUsar.EmpresaRecibeId;
      if (filtrosUsar.PersonaEmiteId) params.PersonaEmiteId = filtrosUsar.PersonaEmiteId;
      if (filtrosUsar.PersonaRecibeId) params.PersonaRecibeId = filtrosUsar.PersonaRecibeId;
      if (filtrosUsar.MonedaId) params.MonedaId = filtrosUsar.MonedaId;
      if (filtrosUsar.MonedaTexto) params.MonedaTexto = filtrosUsar.MonedaTexto;
      if (filtrosUsar.ConceptoId) params.ConceptoId = filtrosUsar.ConceptoId;
      if (filtrosUsar.FormaPagoId) params.FormaPagoId = filtrosUsar.FormaPagoId;
      if (filtrosUsar.Folio) params.Folio = filtrosUsar.Folio;

      if (filtrosUsar.PendientesFirma) {
        params.PendientesFirma = true;
      }

      if (filtrosUsar.EstatusTexto) {
        params.EstatusTexto = filtrosUsar.EstatusTexto;
      }

      const res = await api.get('/recibos', { params });

      setRecibos(res.data || []);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function limpiarFiltros() {
    const filtrosLimpios = {
      FechaInicio: '',
      FechaFin: '',
      EmpresaEmiteId: '',
      PersonaEmiteId: '',
      EmpresaRecibeId: '',
      PersonaRecibeId: '',
      MonedaId: '',
      MonedaTexto: '',
      ConceptoId: '',
      FormaPagoId: '',
      Folio: '',
      PendientesFirma: false,
      EstatusTexto: ''
    };

    setFiltros(filtrosLimpios);
    setMensaje('');
    navigate('/historico');
    buscarRecibos(filtrosLimpios);
  }

  async function verPdf(reciboId) {
    try {
      await abrirPdfRecibo(reciboId);
    } catch (error) {
      setMensaje(
        error.response?.data?.mensaje ||
        'No fue posible abrir el PDF.'
      );
    }
  }

  function verDetalle(reciboId) {
    navigate(`/recibos/${reciboId}`);
  }

  function abrirModalCancelacion(reciboId) {
    setCancelacionModal({
      abierto: true,
      reciboId
    });
  }

  function cerrarModalCancelacion() {
    setCancelacionModal({
      abierto: false,
      reciboId: null
    });
  }

  async function cancelarRecibo(motivo) {
    if (!motivo) {
      setAvisoCancelacionModal(true);
      return;
    }

    const reciboId = cancelacionModal.reciboId;

    if (!reciboId) return;

    cerrarModalCancelacion();

    try {
      await api.put(`/recibos/${reciboId}/cancelar`, {
        MotivoCancelacion: motivo,
        motivo
      });

      setMensaje('Recibo cancelado correctamente.');
      buscarRecibos();
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function exportarExcel() {
    if (!recibos || recibos.length === 0) {
      setMensaje('No hay recibos para exportar con los filtros actuales.');
      return;
    }

    const columnas = [
      'Folio',
      'Fecha',
      'Tipo de recibo',
      'Empresa emite',
      'Persona emite',
      'Empresa recibe',
      'Persona recibe',
      'Concepto',
      'Moneda',
      'Importe',
      'Forma de pago',
      'Referencia',
      'Estatus',
      'Firmado',
      'Cancelado',
      'Observaciones',
      'Ruta PDF',
      'Ruta firmado'
    ];

    const filas = recibos.map((item) => ({
      Folio: item.Folio || '',
      Fecha: formatoFecha(item.Fecha || item.FechaTexto || item.FechaCreacion),
      'Tipo de recibo': item.TipoRecibo || '',
      'Empresa emite': item.EmpresaEmite || '',
      'Persona emite': item.PersonaEmite || '',
      'Empresa recibe': item.EmpresaRecibe || '',
      'Persona recibe': item.PersonaRecibe || '',
      Concepto: item.Concepto || '',
      Moneda: item.Moneda || '',
      Importe: Number(item.Importe || 0),
      'Forma de pago': item.FormaPago || '',
      Referencia: item.NumeroReferencia || '',
      Estatus: item.Estatus || 'Emitido',
      Firmado: item.PdfFirmadoPath ? 'Sí' : 'No',
      Cancelado: item.Cancelado ? 'Sí' : 'No',
      Observaciones: item.Observaciones || '',
      'Ruta PDF': item.PdfPath || '',
      'Ruta firmado': item.PdfFirmadoPath || ''
    }));

    const filtrosTexto = [
      filtros.FechaInicio ? `Fecha inicio: ${filtros.FechaInicio}` : '',
      filtros.FechaFin ? `Fecha fin: ${filtros.FechaFin}` : '',
      filtros.Folio ? `Folio: ${filtros.Folio}` : '',
      filtros.EstatusTexto ? `Estatus: ${filtros.EstatusTexto}` : '',
      filtros.PendientesFirma ? 'Pendientes de firma' : ''
    ].filter(Boolean).join(' | ') || 'Sin filtros descriptivos';

    const encabezados = columnas
      .map((columna) => `<th>${escaparHtmlExcel(columna)}</th>`)
      .join('');

    const cuerpo = filas.map((fila) => {
      const celdas = columnas.map((columna) => {
        if (columna === 'Importe') {
          return `<td style="mso-number-format:'#,##0.00';">${fila[columna]}</td>`;
        }

        return `<td style="mso-number-format:'\\@';">${escaparHtmlExcel(valorTextoExcel(fila[columna]))}</td>`;
      }).join('');

      return `<tr>${celdas}</tr>`;
    }).join('');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            table {
              border-collapse: collapse;
              font-family: Arial, sans-serif;
              font-size: 12px;
            }

            th {
              background: #1f4e78;
              color: #ffffff;
              font-weight: bold;
              border: 1px solid #d9e2f3;
              padding: 6px;
            }

            td {
              border: 1px solid #d9e2f3;
              padding: 5px;
              vertical-align: top;
            }

            .titulo {
              font-size: 18px;
              font-weight: bold;
              color: #1f2937;
            }

            .subtitulo {
              font-size: 12px;
              color: #4b5563;
            }
          </style>
        </head>

        <body>
          <table>
            <tr>
              <td class="titulo" colspan="${columnas.length}">
                Sistema Recibos - Histórico
              </td>
            </tr>
            <tr>
              <td class="subtitulo" colspan="${columnas.length}">
                Generado: ${escaparHtmlExcel(new Date().toLocaleString('es-MX'))}
              </td>
            </tr>
            <tr>
              <td class="subtitulo" colspan="${columnas.length}">
                Filtros: ${escaparHtmlExcel(filtrosTexto)}
              </td>
            </tr>
            <tr></tr>
            <tr>${encabezados}</tr>
            ${cuerpo}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], {
      type: 'application/vnd.ms-excel;charset=utf-8;'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `Historico_Recibos_${fechaHoraArchivo()}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setMensaje(`Excel generado con ${recibos.length} registros.`);
  }

  function etiquetaFiltroActivo() {
    if (filtros.PendientesFirma) return 'Pendientes de firma';
    if (filtros.EstatusTexto) return `Estatus: ${filtros.EstatusTexto}`;
    if (filtros.MonedaTexto) return `Moneda: ${filtros.MonedaTexto}`;
    if (location.search.includes('mesActual=1')) return 'Recibos del mes';
    if (location.search.includes('hoy=1')) return 'Recibos de hoy';

    return '';
  }

  return (
    <div className="page-container-normal">
      <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h1>Histórico</h1>
            <div className="text-muted">
              Consulta, filtros y seguimiento de recibos emitidos
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <button
              className="btn btn-outline-success"
              type="button"
              onClick={exportarExcel}
              disabled={recibos.length === 0}
            >
              Exportar Excel
            </button>

            <button
              className="btn btn-primary"
              type="button"
              onClick={() => navigate('/recibos')}
            >
              Nuevo recibo
            </button>
          </div>
        </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {etiquetaFiltroActivo() && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center">
          <div>
            Filtro aplicado desde Dashboard: <strong>{etiquetaFiltroActivo()}</strong>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={limpiarFiltros}
          >
            Quitar filtro
          </button>
        </div>
      )}

      <div className="card-box mb-3" style={{ paddingTop: '12px', paddingBottom: '12px' }}>
        <h5 className="mb-3">Búsqueda avanzada</h5>

        <form onSubmit={(e) => buscarRecibos(null, e)}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '0.85fr 2.15fr 2.15fr 1.6fr 1.6fr',
              columnGap: '10px',
              rowGap: '10px',
              alignItems: 'end',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {/* FILA 1 */}
            <div>
              <label className="form-label">Fecha inicio</label>
              <input
                className="form-control"
                type="date"
                name="FechaInicio"
                value={filtros.FechaInicio}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="form-label">Empresa Emite</label>
              <select
                className="form-select"
                name="EmpresaEmiteId"
                value={filtros.EmpresaEmiteId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {empresas.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Empresa Recibe</label>
              <select
                className="form-select"
                name="EmpresaRecibeId"
                value={filtros.EmpresaRecibeId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {empresas.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Moneda</label>
              <select
                className="form-select"
                name="MonedaId"
                value={filtros.MonedaId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {monedas.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Estatus</label>
              <select
                className="form-select"
                name="EstatusTexto"
                value={filtros.EstatusTexto}
                onChange={handleChange}
              >
                <option value="">Todos</option>
                <option value="Emitido">Emitido</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Pagado">Pagado</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </div>

            {/* FILA 2 */}
            <div>
              <label className="form-label">Fecha fin</label>
              <input
                className="form-control"
                type="date"
                name="FechaFin"
                value={filtros.FechaFin}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="form-label">Persona Emite</label>
              <select
                className="form-select"
                name="PersonaEmiteId"
                value={filtros.PersonaEmiteId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {personasEmisoras.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Persona Recibe</label>
              <select
                className="form-select"
                name="PersonaRecibeId"
                value={filtros.PersonaRecibeId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {personasReceptoras.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Forma de Pago</label>
              <select
                className="form-select"
                name="FormaPagoId"
                value={filtros.FormaPagoId}
                onChange={handleChange}
              >
                <option value="">Todas</option>

                {formasPago.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Concepto</label>
              <select
                className="form-select"
                name="ConceptoId"
                value={filtros.ConceptoId}
                onChange={handleChange}
              >
                <option value="">Todos</option>

                {conceptos.map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            {/* FILA 3 */}
            <div>
              <label className="form-label">Folio</label>
            </div>

            <div>
              <input
                className="form-control"
                name="Folio"
                value={filtros.Folio}
                onChange={handleChange}
                placeholder="Ej. MCM-2026-0001"
              />
            </div>

            <div />

            <div>
              <button
                className="btn btn-primary w-100"
                type="submit"
              >
                Buscar
              </button>
            </div>

            <div>
              <button
                className="btn btn-outline-secondary w-100"
                type="button"
                onClick={limpiarFiltros}
              >
                Limpiar
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Recibos emitidos</h5>

          <span className="badge bg-secondary">
            {recibos.length} registros
          </span>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Emite</th>
                <th>Recibe</th>
                <th>Concepto</th>
                <th style={{ textAlign: 'right' }}>Importe</th>
                <th
                  style={{
                    textAlign: 'center',
                    width: '130px'
                  }}
                >
                  Estatus
                </th>
                <th style={{ width: '220px' }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {recibos.map(item => (
                <tr key={item.Id}>
                  <td>
                    <strong>{item.Folio}</strong>
                  </td>

                  <td>{formatoFecha(item.Fecha)}</td>

                  <td>
                    {item.EmpresaEmite || item.PersonaEmite || 'Sin empresa'}
                  </td>

                  <td>
                    {item.EmpresaRecibe || item.PersonaRecibe || '-'}
                  </td>

                  <td>{item.Concepto}</td>

                  <td style={{ textAlign: 'right' }}>
                    {formatoImporte(item.Importe, item.Moneda)}
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <span
                      className={
                        item.Estatus === 'Cancelado'
                          ? 'badge bg-danger'
                          : item.Estatus === 'Pagado'
                          ? 'badge bg-success'
                          : item.Estatus === 'Pendiente'
                          ? 'badge bg-warning text-dark'
                          : 'badge bg-primary'
                      }
                    >
                      {item.Estatus || 'Emitido'}
                    </span>
                  </td>

                  <td>
                    <div className="d-flex gap-1 flex-wrap">

                      <button
                        className="btn btn-sm btn-outline-primary"
                        type="button"
                        onClick={() => verDetalle(item.Id)}
                        title="Ver detalle"
                      >
                        Detalle
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        title="Duplicar recibo"
                        onClick={() => {
                          navigate(`/recibos?duplicar=${item.Id}`);
                        }}
                      >
                        ⧉
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => verPdf(item.Id)}
                      >
                        PDF
                      </button>

                      {!item.Cancelado && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          type="button"
                          onClick={() => abrirModalCancelacion(item.Id)}
                        >
                          Cancelar
                        </button>
                      )}

                    </div>
                  </td>
                </tr>
              ))}

              {recibos.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No hay recibos para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AppModal
        abierto={cancelacionModal.abierto}
        titulo="Sistema Recibos"
        mensaje="Esta acción quedará registrada en la bitácora del recibo."
        variante="danger"
        mostrarInput
        inputLabel="Motivo de cancelación"
        inputPlaceholder="Captura el motivo de cancelación..."
        textoCancelar="Conservar recibo"
        textoAceptar="Cancelar recibo"
        onCancelar={cerrarModalCancelacion}
        onAceptar={cancelarRecibo}
      />

      <AppModal
        abierto={avisoCancelacionModal}
        titulo="Sistema Recibos"
        mensaje="Capture el motivo de cancelación."
        variante="warning"
        textoCancelar="Cerrar"
        textoAceptar="Aceptar"
        onCancelar={() => setAvisoCancelacionModal(false)}
        onAceptar={() => setAvisoCancelacionModal(false)}
      />
    </div>
  );
}

export default HistoricoPage;
